import { useState, useCallback } from "react";
import useSWR from "swr";
import dayjs from "dayjs";
import { getTaskInfo } from "./task-info-service";
import { UseTaskInfoModel, TaskInfoParams } from "./types";
import { FiltersState } from "./use-task-info-filters";
import { statusKeys, tableList } from "./task-constants";

const mapFiltersToParams = (filters: FiltersState): TaskInfoParams => {
  const tableIndex = tableList.findIndex(
    (table) => table.key === filters.activeTable,
  );

  return {
    tableName: Math.max(0, tableIndex),
    statuses: filters.activeStatuses.length
      ? filters.activeStatuses.map((s) => Number(s))
      : statusKeys.map((k) => Number(k)),
    dateTo: filters.dateTo,
    dateFrom: filters.dateFrom,
  };
};

const useTaskInfo = (
  filters: FiltersState,
): UseTaskInfoModel & { fetchTaskInfo: () => Promise<void> } => {
  const [searchKey, setSearchKey] = useState<string | null>(null);

  const { data, error, isLoading } = useSWR(
    searchKey,
    async (key: string) => {
      const params = JSON.parse(key.replace("/task/", ""));
      return getTaskInfo(params);
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const fetchTaskInfo = useCallback(async () => {
    // Always use current time for dateTo when fetching
    const updatedFilters = {
      ...filters,
      dateTo: dayjs().toISOString(),
    };
    const params = mapFiltersToParams(updatedFilters);
    const key = `/task/${JSON.stringify(params)}`;
    setSearchKey(key);
  }, [filters]);

  return {
    data: data?.results,
    isLoading,
    error,
    mutate: fetchTaskInfo,
    fetchTaskInfo,
  };
};

export default useTaskInfo;
